import type jsPDF from 'jspdf';
import type { UserOptions } from 'jspdf-autotable';
import { COLORS, LAYOUT } from './reportConstants';
import type { ReportResultItem } from '@/api/adminService';

declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: UserOptions) => jsPDF;
    lastAutoTable: { finalY: number };
  }
}

export interface ChatGptReportData {
  domain: string;
  businessName: string;
  results: ReportResultItem[];
}

const CHATGPT_COLOR = '#10a37f';

/**
 * Sanitize text for jsPDF's built-in Helvetica which only supports
 * WinAnsiEncoding (basic Latin). Replace Unicode chars that cause
 * broken letter-spacing / missing glyphs.
 */
function sanitize(text: string): string {
  return text
    // Smart quotes -> straight quotes
    .replace(/[\u2018\u2019\u201A]/g, "'")
    .replace(/[\u201C\u201D\u201E]/g, '"')
    // Dashes
    .replace(/[\u2013]/g, '-')   // en dash
    .replace(/[\u2014]/g, '--')  // em dash
    // Ellipsis
    .replace(/[\u2026]/g, '...')
    // Tilde / approximate
    .replace(/[\u223C]/g, '~')
    // Bullets
    .replace(/[\u2022\u2023\u25E6]/g, '-')
    // Arrows
    .replace(/[\u2192\u2190\u2194]/g, '->')
    .replace(/[\u21D2]/g, '=>')
    // Misc symbols
    .replace(/[\u2713\u2714]/g, '[ok]')   // checkmarks
    .replace(/[\u2717\u2718]/g, '[x]')    // crosses
    .replace(/[\u00A0]/g, ' ')            // non-breaking space
    // Strip markdown link syntax: [text](url) -> text (url)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
    // Remove any remaining chars outside basic Latin + Latin-1 Supplement
    // that jsPDF can't render (keep 0x20-0x7E and 0xA0-0xFF)
    .replace(/[^\x20-\x7E\xA0-\xFF\n\r\t]/g, ' ');
}

export async function generateChatGptReport(data: ChatGptReportData): Promise<jsPDF> {
  const { jsPDF } = await import('jspdf');
  const { applyPlugin } = await import('jspdf-autotable');
  applyPlugin(jsPDF);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const { margin, contentWidth, pageHeight } = LAYOUT;
  const bottomMargin = 20;
  const textAreaWidth = contentWidth - 8; // padding inside response boxes
  let y = margin;

  /** Add a new page and reset y to top margin */
  const newPage = () => {
    doc.addPage();
    y = margin;
  };

  /** If not enough space, start a new page */
  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - bottomMargin) {
      newPage();
    }
  };

  // ─── HEADER ───
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLORS.black);
  doc.text('BY', margin, y);
  const byWidth = doc.getTextWidth('BY');
  doc.setTextColor(COLORS.primary);
  doc.text('LLMS', margin + byWidth, y);
  doc.setTextColor(COLORS.black);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLORS.textMuted);
  const dateText = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  doc.text(dateText, margin + contentWidth, y, { align: 'right' });

  y += 4;
  doc.setFontSize(8);
  doc.setTextColor(COLORS.primary);
  doc.text(sanitize(data.domain), margin, y);
  doc.setTextColor(CHATGPT_COLOR);
  doc.text('ChatGPT Visibility Report', margin + contentWidth, y, { align: 'right' });

  y += 4;
  doc.setDrawColor(COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(margin, y, margin + contentWidth, y);
  y += 10;

  // ─── TITLE ───
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLORS.black);
  doc.text('ChatGPT Visibility Report', margin, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLORS.textMuted);
  doc.text(sanitize(`Business: ${data.businessName}`), margin, y);
  y += 10;

  // ─── SUMMARY METRICS ───
  const totalQueries = data.results.length;
  const mentionedCount = data.results.filter((r) => r.mentioned).length;
  const mentionRate = totalQueries > 0 ? Math.round((mentionedCount / totalQueries) * 100) : 0;
  const urlFoundCount = data.results.filter((r) => r.urlFound).length;
  const nameFoundCount = data.results.filter((r) => r.businessNameFound).length;

  const metricBoxWidth = (contentWidth - 12) / 4;
  const metricBoxHeight = 28;
  const metricsData = [
    { label: 'QUERIES TESTED', value: `${totalQueries}` },
    { label: 'PRESENCE RATE', value: `${mentionRate}%` },
    { label: 'URL CITED', value: `${urlFoundCount}` },
    { label: 'NAME MENTIONED', value: `${nameFoundCount}` },
  ];

  metricsData.forEach((metric, i) => {
    const x = margin + i * (metricBoxWidth + 4);
    doc.setFillColor(COLORS.gray);
    doc.setDrawColor(COLORS.border);
    doc.roundedRect(x, y, metricBoxWidth, metricBoxHeight, 1, 1, 'FD');

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.black);
    doc.text(metric.value, x + metricBoxWidth / 2, y + 12, { align: 'center' });

    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.textMuted);
    doc.text(metric.label, x + metricBoxWidth / 2, y + 20, { align: 'center' });
  });

  y += metricBoxHeight + LAYOUT.sectionGap;

  // ─── RESULTS BY CATEGORY ───
  const groupedResults: Record<string, ReportResultItem[]> = {};
  for (const r of data.results) {
    if (!groupedResults[r.category]) groupedResults[r.category] = [];
    groupedResults[r.category].push(r);
  }

  for (const [category, catResults] of Object.entries(groupedResults)) {
    ensureSpace(30);

    // Category heading with green accent bar
    doc.setFillColor(CHATGPT_COLOR);
    doc.rect(margin, y, 2, 6, 'F');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.black);
    doc.text(sanitize(category.toUpperCase()), margin + 6, y + 4.5);

    const catMentioned = catResults.filter((r) => r.mentioned).length;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLORS.textMuted);
    doc.text(`${catMentioned}/${catResults.length} present`, margin + contentWidth, y + 4.5, { align: 'right' });

    y += 10;

    // Summary table for category
    const tableBody = catResults.map((r) => [
      sanitize(r.query),
      r.mentioned ? 'Present' : 'Not Found',
      r.urlFound ? 'Yes' : 'No',
      r.businessNameFound ? 'Yes' : 'No',
    ]);

    doc.autoTable({
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Query', 'Status', 'URL Cited', 'Name Found']],
      body: tableBody,
      theme: 'plain',
      styles: {
        fontSize: 8,
        cellPadding: 2.5,
        textColor: COLORS.black,
        lineColor: COLORS.border,
        lineWidth: 0.2,
        font: 'helvetica',
      },
      headStyles: {
        fillColor: COLORS.gray,
        textColor: COLORS.textMuted,
        fontSize: 6,
        fontStyle: 'bold',
        cellPadding: 2.5,
      },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 22, halign: 'center' },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 26, halign: 'center' },
      },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && hookData.column.index === 1) {
          const val = hookData.cell.raw as string;
          hookData.cell.styles.textColor = val === 'Present' ? COLORS.green : COLORS.red;
          hookData.cell.styles.fontStyle = 'bold';
        }
      },
    });

    y = doc.lastAutoTable.finalY + 8;

    // ─── Detailed responses per query ───
    for (const result of catResults) {
      // Query header: needs ~12mm
      ensureSpace(20);

      // Query text + status on one line
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(COLORS.black);
      const safeQuery = sanitize(result.query);
      const queryDisplay = safeQuery.length > 85 ? safeQuery.substring(0, 82) + '...' : safeQuery;
      doc.text(`"${queryDisplay}"`, margin, y);

      const statusText = result.mentioned ? 'PRESENT' : 'NOT FOUND';
      const statusColor = result.mentioned ? COLORS.green : COLORS.red;
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(statusColor);
      doc.text(statusText, margin + contentWidth, y, { align: 'right' });

      y += 5;

      // Response text — render line by line with page breaks
      if (result.responseText) {
        const safeText = sanitize(result.responseText);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        const allLines: string[] = doc.splitTextToSize(safeText, textAreaWidth);
        const lineHeight = 3.2;
        const boxPadX = 4;
        const boxPadY = 3;

        // Render lines in page-fitting chunks
        let lineIdx = 0;
        while (lineIdx < allLines.length) {
          const available = pageHeight - bottomMargin - y - boxPadY * 2;
          const fitCount = Math.max(1, Math.floor(available / lineHeight));
          const chunk = allLines.slice(lineIdx, lineIdx + fitCount);
          const chunkHeight = chunk.length * lineHeight + boxPadY * 2;

          // Gray background box
          doc.setFillColor(COLORS.gray);
          doc.setDrawColor(COLORS.border);
          doc.roundedRect(margin, y, contentWidth, chunkHeight, 1, 1, 'FD');

          // Text
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(COLORS.black);
          let textY = y + boxPadY + lineHeight * 0.7;
          for (const line of chunk) {
            doc.text(line, margin + boxPadX, textY);
            textY += lineHeight;
          }

          y += chunkHeight + 2;
          lineIdx += fitCount;

          if (lineIdx < allLines.length) {
            newPage();
          }
        }
      }

      // Citations
      if (result.citations.length > 0) {
        const citationsToShow = result.citations.slice(0, 5);
        const citHeight = 4 + citationsToShow.length * 3.5;
        ensureSpace(citHeight);

        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(COLORS.textMuted);
        doc.text(`CITATIONS (${result.citations.length})`, margin + 4, y);
        y += 3.5;

        for (const cit of citationsToShow) {
          ensureSpace(4);
          doc.setFontSize(6);
          doc.setFont('helvetica', 'normal');
          const isDomainCitation = cit.url.toLowerCase().includes(data.domain);
          doc.setTextColor(isDomainCitation ? COLORS.green : '#3b82f6');
          const citText = sanitize((cit.title || cit.url).substring(0, 90));
          doc.text(`- ${citText}`, margin + 4, y);
          y += 3.5;
        }
      }

      y += 6; // gap between query blocks
    }

    y += 6; // gap between categories
  }

  // ─── FOOTER ───
  ensureSpace(30);

  doc.setDrawColor(COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(margin, y, margin + contentWidth, y);
  y += 10;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLORS.black);
  doc.text('BY', margin + contentWidth / 2 - doc.getTextWidth('BYLLMS') / 2, y);
  const footerByWidth = doc.getTextWidth('BY');
  doc.setTextColor(COLORS.primary);
  doc.text('LLMS', margin + contentWidth / 2 - doc.getTextWidth('BYLLMS') / 2 + footerByWidth, y);
  y += 6;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLORS.textMuted);
  const genDate = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  doc.text(`Generated on ${genDate}`, margin + contentWidth / 2, y, { align: 'center' });
  y += 5;
  doc.setFontSize(7);
  doc.text('Confidential - For internal use only', margin + contentWidth / 2, y, { align: 'center' });

  return doc;
}
